/**
 * Proof that the player holds the wallet they are registering.
 *
 * `user/setNickNameWithWallet` creates the account for the wallet in the request body and returns
 * its session cookies, so the server refuses a wallet it has no proof of. The signed text must
 * match `buildWalletSignupMessage` in `packages/core/src/utils/solanaMessageVerify.js` byte for
 * byte — the server rebuilds it from its own values and compares the whole string.
 */
export type SolanaSignMessage = (message: Uint8Array) => Promise<Uint8Array>

export interface WalletProof {
  message: string
  signatureBase64: string
}

/** Server-side codes that mean "this request needs a wallet signature". */
export const WALLET_PROOF_CODES = [
  'WALLET_PROOF_REQUIRED',
  'WALLET_PROOF_EXPIRED',
  'WALLET_PROOF_MISMATCH',
  'WALLET_PROOF_INVALID',
] as const

export function isWalletProofRejection(payload: unknown): boolean {
  const code = (payload as { code?: unknown } | null | undefined)?.code
  return typeof code === 'string' && (WALLET_PROOF_CODES as readonly string[]).includes(code)
}

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary)
}

async function sign(message: string, signMessage: SolanaSignMessage): Promise<WalletProof> {
  const signature = await signMessage(new TextEncoder().encode(message))
  return { message, signatureBase64: toBase64(new Uint8Array(signature)) }
}

export async function buildWalletSignupProof(
  walletAddress: string,
  nickname: string,
  signMessage: SolanaSignMessage,
): Promise<WalletProof> {
  const ts = Date.now()
  return sign(
    `Eclipsera sign up\nWallet: ${walletAddress}\nNickname: ${nickname}\nTimestamp: ${ts}`,
    signMessage,
  )
}

export async function buildWalletLoginProof(
  walletAddress: string,
  signMessage: SolanaSignMessage,
): Promise<WalletProof> {
  const ts = Date.now()
  return sign(`Eclipsera login\nWallet: ${walletAddress}\nTimestamp: ${ts}`, signMessage)
}
